/** Categories that use the camera-style capture button (same pattern as announcement files). */
const MEDIA_CATEGORIES = new Set(['photo', 'video']);

const ACCEPT_BY_CATEGORY: Record<string, string[]> = {
  photo: ['image/*'],
  video: ['video/*'],
  audio: ['audio/*'],
  pdf: ['application/pdf', '.pdf'],
  word: [
    '.doc',
    '.docx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  ppt: [
    '.ppt',
    '.pptx',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
};

function uniqueAcceptParts(types: string[]): string {
  const parts: string[] = [];
  for (const t of types) {
    const chunk = ACCEPT_BY_CATEGORY[t];
    if (chunk) parts.push(...chunk);
  }
  return [...new Set(parts)].join(',');
}

export function buildPublicFileUploadInputs(allowedTypes: string[]): {
  showCamera: boolean;
  showDocuments: boolean;
  cameraAccept: string;
  documentsAccept: string;
} {
  const mediaAllowed = allowedTypes.filter(t => MEDIA_CATEGORIES.has(t));
  const docAllowed = allowedTypes.filter(t => !MEDIA_CATEGORIES.has(t));
  return {
    showCamera: mediaAllowed.length > 0,
    showDocuments: docAllowed.length > 0,
    cameraAccept: uniqueAcceptParts(mediaAllowed),
    documentsAccept: uniqueAcceptParts(docAllowed),
  };
}

/** Which configured categories this file matches (mime + extension). */
export function getDetectedCategories(file: File): string[] {
  const mime = (file.type || '').toLowerCase();
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const found = new Set<string>();

  if (
    mime.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'heic', 'bmp', 'svg'].includes(ext)
  ) {
    found.add('photo');
  }
  if (
    mime.startsWith('video/') ||
    ['mp4', 'mov', 'webm', 'mkv', 'avi'].includes(ext)
  ) {
    found.add('video');
  }
  if (
    mime.startsWith('audio/') ||
    ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac'].includes(ext)
  ) {
    found.add('audio');
  }
  if (mime === 'application/pdf' || ext === 'pdf') {
    found.add('pdf');
  }
  if (
    mime === 'application/msword' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ['doc', 'docx'].includes(ext)
  ) {
    found.add('word');
  }
  if (
    mime === 'application/vnd.ms-powerpoint' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    ['ppt', 'pptx'].includes(ext)
  ) {
    found.add('ppt');
  }

  return [...found];
}

export function fileMatchesAllowedTypes(file: File, allowed: string[]): boolean {
  if (!allowed.length) return false;
  const detected = getDetectedCategories(file);
  return detected.some(d => allowed.includes(d));
}

export type PublicFileUploadErrorCode = 'invalidType' | 'tooLarge' | 'tooMany';

export function getPublicFileUploadAnswerError(
  val: unknown,
  cfg: {allowedTypes: string[]; maxFileSizeMB: number; maxFiles: number},
): PublicFileUploadErrorCode | null {
  if (!Array.isArray(val) || val.length === 0) return null;
  if (!val.every(f => typeof File !== 'undefined' && f instanceof File)) {
    return null;
  }
  if (val.length > cfg.maxFiles) return 'tooMany';
  const maxBytes = cfg.maxFileSizeMB * 1024 * 1024;
  for (const f of val) {
    if (f.size > maxBytes) return 'tooLarge';
    if (!fileMatchesAllowedTypes(f, cfg.allowedTypes)) return 'invalidType';
  }
  return null;
}
